import { Extension, Node, mergeAttributes } from '@tiptap/core';
import { ReactNodeViewRenderer } from '@tiptap/react';
import Suggestion, { SuggestionOptions } from '@tiptap/suggestion';
import { Selection } from 'prosemirror-state';
import { Node as ProseMirrorNode } from 'prosemirror-model';
import { PluginKey } from 'prosemirror-state';
import Paragraph from '@tiptap/extension-paragraph';

import RichtextCustomReadonlyPlaceholder from './richtext-custom-readonly-placeholder';
import { FontFamily } from './richtext-toolbar.constants';

export const FONT_CLASS_PREFIX = 'font';
export const READONLY_PLACEHOLDER_NODE = 'readonlyPlaceholder';

type TypesOptions = {
  types: string[];
};

declare module '@tiptap/core' {
  interface Commands<ReturnType> {
    fontFamily: {
      setFontFamily: (fontFamily: FontFamily) => ReturnType;
      unsetFontFamily: () => ReturnType;
    };
    small: {
      setSmall: () => ReturnType;
    };
  }
}

// CustomFontFamily is a copy of @tiptap/extension-font-family
// but in addition adds a class so that we can target specific fonts in our styled components
export const CustomFontFamily = Extension.create<TypesOptions, any>({
  name: 'customFontFamily',

  addOptions() {
    return {
      types: ['textStyle'],
    };
  },

  addGlobalAttributes() {
    return [
      {
        types: this.options.types,
        attributes: {
          fontFamily: {
            default: null,
            parseHTML: element =>
              element.style.fontFamily.replace(/['"]+/g, ''),
            renderHTML: attributes => {
              if (!attributes.fontFamily) {
                return {};
              }

              return {
                class: `${FONT_CLASS_PREFIX}-${attributes.fontFamily}`,
                style: `font-family: ${attributes.fontFamily}`,
              };
            },
          },
        },
      },
    ];
  },

  addCommands() {
    return {
      setFontFamily:
        fontFamily =>
        ({ chain }) => {
          return chain().setMark('textStyle', { fontFamily }).run();
        },
      unsetFontFamily:
        () =>
        ({ chain }) => {
          return chain()
            .setMark('textStyle', { fontFamily: null })
            .removeEmptyTextStyle()
            .run();
        },
    };
  },
});

export type HashtagLinkOptions = {
  HTMLAttributes: Record<string, any>;
  renderLabel: (props: {
    options: HashtagLinkOptions;
    node: ProseMirrorNode;
  }) => string;
  suggestion: Omit<SuggestionOptions, 'editor'>;
};

export const HashtagLinks = Node.create<HashtagLinkOptions>({
  name: 'hashtag-links',

  addOptions() {
    return {
      HTMLAttributes: {},
      renderLabel({ options, node }) {
        return `${options.suggestion.char}${node.attrs.name}`;
      },
      suggestion: {
        char: '#',
        pluginKey: new PluginKey(this.name),
        command: ({ editor, range, props }) => {
          // increase range.to by one when the next node is of type "text"
          // and starts with a space character
          const nodeAfter = editor.view.state.selection.$to.nodeAfter;
          const overrideSpace = nodeAfter?.text?.startsWith(' ');

          if (overrideSpace) {
            range.to += 1;
          }

          editor
            .chain()
            .focus()
            .insertContentAt(range, [
              {
                type: this.name,
                attrs: props,
              },
              {
                type: 'text',
                text: ' ',
              },
            ])
            .run();
        },
        allow: ({ state, range }) => {
          const $from = state.doc.resolve(range.from);
          const type = state.schema.nodes[this.name];
          const allow = !!$from.parent.type.contentMatch.matchType(type);

          return allow;
        },
      },
    };
  },
  group: 'inline',
  inline: true,
  selectable: false,
  atom: true,
  addAttributes() {
    return {
      link: {
        default: null,
        parseHTML: (element: HTMLAnchorElement) => element.href,
        renderHTML: attributes => {
          if (!attributes.link) {
            return {};
          }

          return {
            href: attributes.link,
          };
        },
      },
      name: {
        default: null,
        parseHTML: element => element.getAttribute('data-name'),
        renderHTML: attributes => {
          if (!attributes.name) {
            return {};
          }

          return {
            'data-name': attributes.name,
          };
        },
      },
    };
  },

  parseHTML() {
    return [
      {
        tag: `a[data-type="${this.name}"]`,
      },
    ];
  },

  renderHTML({ node, HTMLAttributes }) {
    return [
      'a',
      mergeAttributes(
        { 'data-type': this.name },
        this.options.HTMLAttributes,
        HTMLAttributes
      ),
      this.options.renderLabel({
        options: this.options,
        node,
      }),
    ];
  },

  renderText({ node }) {
    return this.options.renderLabel({
      options: this.options,
      node,
    });
  },

  addKeyboardShortcuts() {
    return {
      Backspace: () =>
        this.editor.commands.command(({ tr, state }) => {
          let isMention = false;
          const { selection } = state;
          const { empty, anchor } = selection;

          if (!empty) {
            return false;
          }

          state.doc.nodesBetween(anchor - 1, anchor, (node, pos) => {
            if (node.type.name === this.name) {
              isMention = true;
              tr.insertText(
                this.options.suggestion.char || '',
                pos,
                pos + node.nodeSize
              );

              return false;
            }
          });

          return isMention;
        }),
    };
  },

  addProseMirrorPlugins() {
    return [
      Suggestion({
        editor: this.editor,
        ...this.options.suggestion,
      }),
    ];
  },
});

export interface SmallOptions {
  HTMLAttributes: Record<string, any>;
}

export const SmallParagraph = Paragraph.extend({
  name: 'small',

  priority: 1000,

  addOptions() {
    return {
      HTMLAttributes: {
        class: 'small',
      },
    };
  },

  addCommands() {
    return {
      setSmall:
        () =>
        ({ commands }) => {
          return commands.setNode(this.name);
        },
    };
  },
});

export const ReadonlyPlaceholder = Node.create({
  name: READONLY_PLACEHOLDER_NODE,

  group: 'inline',

  inline: true,

  selectable: false,

  atom: true,

  addAttributes() {
    return {
      text: {
        default: '',
      },
    };
  },

  parseHTML() {
    return [
      {
        tag: 'span.node-readonlyPlaceholder',
      },
    ];
  },

  renderHTML({ node }) {
    return ['span', { class: 'readonly-placeholder' }, node.attrs.text];
  },

  addNodeView() {
    return ReactNodeViewRenderer(RichtextCustomReadonlyPlaceholder);
  },

  onTransaction({ transaction }) {
    // prevent placing the cursor before the placeholder
    const cursorPosition = transaction.selection.$anchor.pos;

    if (cursorPosition === 1 && this.editor.isFocused) {
      this.editor.chain().focus().setTextSelection(2).run();
    }
  },

  onSelectionUpdate() {
    const { from, to } = this.editor.state.selection;
    // check if the selection include readonly
    if (from === 1) {
      this.editor.commands.setTextSelection({ from: 2, to });
    }
  },

  addKeyboardShortcuts() {
    return {
      'Mod-a': () => {
        this.editor.commands.setTextSelection({ from: 2, to: 1000000 });
        return true;
      },
      'Cmd-Shift-Backspace': () =>
        this.editor.commands.command(() => {
          return true;
        }),
      'Control-Backspace': () =>
        this.editor.commands.command(({ state }) =>
          canPerformAction(state.selection)
        ),
      'Cmd-Backspace': () =>
        this.editor.commands.command(({ state }) =>
          canPerformAction(state.selection)
        ),
      Delete: () =>
        this.editor.commands.command(({ state }) =>
          canPerformAction(state.selection)
        ),
      Backspace: () =>
        this.editor.commands.command(({ state }) =>
          canPerformAction(state.selection)
        ),
    };
  },
});

function canPerformAction(selection: Selection) {
  if (
    selection.ranges[0].$from.pos < 2 ||
    (selection.ranges[0].$from.pos === 2 && selection.ranges[0].$to.pos === 2)
  ) {
    return true;
  }
  return false;
}
